import { useAuth, ChainlitContext, useSubscription, useChatData } from '@chainlit/react-client';
import { useContext, useEffect, useState, useCallback, useRef } from 'react';

interface MessageStatus {
  messageCount: number;
  limit: number;
  canSend: boolean;
  remaining: number;
}

interface UseMessageLimitReturn {
  status: MessageStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isLimitReached: boolean;
}

// Global state to prevent duplicate requests
let globalState: {
  status: MessageStatus | null;
  loading: boolean;
  error: string | null;
  lastFetch: number;
  promise: Promise<void> | null;
} = {
  status: null,
  loading: false,
  error: null,
  lastFetch: 0,
  promise: null
};

const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach(callback => callback());
}

// Call this immediately when user sends a message for instant UI feedback
export function decrementMessageLimit() {
  if (!globalState.status || globalState.status.limit === -1) return;
  globalState.status = {
    ...globalState.status,
    messageCount: globalState.status.messageCount + 1,
    remaining: Math.max(0, globalState.status.remaining - 1),
    canSend: globalState.status.remaining - 1 > 0
  };
  notifySubscribers();
}

export function useMessageLimit(): UseMessageLimitReturn {
  const { user } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const [, forceUpdate] = useState({});
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    subscribers.add(triggerUpdate);
    return () => {
      subscribers.delete(triggerUpdate);
    };
  }, [triggerUpdate]);

  const fetchMessageStatus = useCallback(async () => {
    if (!user || !apiClient) return;
    
    const now = Date.now();
    const CACHE_DURATION = 10000; // 10 seconds cache
    
    // Return cached data if still fresh
    if (now - globalState.lastFetch < CACHE_DURATION && globalState.status) {
      return;
    }
    
    // If already fetching, wait for existing request
    if (globalState.promise) {
      return globalState.promise;
    }
    
    globalState.promise = (async () => {
      try {
        globalState.loading = true;
        globalState.error = null;
        notifySubscribers();
        
        const response = await apiClient.get('/api/usage/status');
        const data = await response.json();
        globalState.status = data;
        globalState.lastFetch = now;
      } catch (err: any) {
        console.error('Failed to fetch message status:', err);
        globalState.error = 'Failed to load message status';
      } finally {
        globalState.loading = false;
        globalState.promise = null;
        notifySubscribers();
      }
    })();
    
    return globalState.promise;
  }, [user, apiClient]);

  useEffect(() => {
    fetchMessageStatus();
  }, [fetchMessageStatus]);

  // Refresh status every 60 seconds (reduced from 30s) and only if user is active
  useEffect(() => {
    if (!user) return;
    
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Only refresh if user has sent a message recently or is actively using the app
    intervalRef.current = setInterval(() => {
      // Check if user has been active (moved mouse, typed, etc.)
      const lastActivity = Date.now() - (document.lastActive || 0);
      if (lastActivity < 120000) { // 2 minutes
        fetchMessageStatus();
      }
    }, 60000); // 60 seconds
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user, fetchMessageStatus]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      (document as any).lastActive = Date.now();
    };

    document.addEventListener('mousemove', updateActivity);
    document.addEventListener('keydown', updateActivity);
    document.addEventListener('click', updateActivity);
    
    return () => {
      document.removeEventListener('mousemove', updateActivity);
      document.removeEventListener('keydown', updateActivity);
      document.removeEventListener('click', updateActivity);
    };
  }, []);

  // Watch for AI response completion and refresh accurate count from backend
  const { loading: chatLoading } = useChatData();
  const prevChatLoadingRef = useRef(chatLoading);

  useEffect(() => {
    if (prevChatLoadingRef.current === true && chatLoading === false) {
      // Response stream just completed — invalidate cache and fetch real count
      globalState.lastFetch = 0;
      fetchMessageStatus();
    }
    prevChatLoadingRef.current = chatLoading;
  }, [chatLoading, fetchMessageStatus]);

  const { isProUser } = useSubscription();
  const isAdmin = user?.metadata?.role === 'ADMIN';

  // Only paid subscribers and admins can send messages
  const isLimitReached = isAdmin || isProUser ? false : true;

  return {
    status: globalState.status,
    loading: globalState.loading,
    error: globalState.error,
    refresh: fetchMessageStatus,
    isLimitReached
  };
}